import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { 
  duelService, 
  stakeService, 
  challengeTemplateService,
  friendshipService 
} from '../../../services/socialService';

const CreateDuelModal = ({ isOpen, onClose, onDuelCreated, userId }) => {
  const [step, setStep] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [stakes, setStakes] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [formData, setFormData] = useState({
    templateId: null,
    opponentId: null,
    targetValue: 0,
    durationDays: 7,
    stakeId: null,
    customTitle: '',
    customDescription: '',
    workoutsPerWeek: 3,
    specificDays: [],
    // New custom challenge fields
    isCustom: false,
    customRules: '',
    customReward: '',
    customConsequence: '',
    verificationMode: 'auto',
    verificationTimerMinutes: 2,
    requireDualCamera: false
  });

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const fetchData = async () => {
    try {
      const [templatesData, stakesData, friendsData] = await Promise?.all([
        challengeTemplateService?.getTemplates(),
        stakeService?.getStakes(),
        friendshipService?.getFriends(userId)
      ]);
      setTemplates(templatesData);
      setStakes(stakesData);
      setFriends(friendsData?.map(f => f?.user_profiles) || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleSearchOpponents = async () => {
    if (!searchTerm?.trim()) return;

    setSearching(true);
    try {
      const results = await friendshipService?.searchUsers(searchTerm, userId);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching users:', error);
    } finally {
      setSearching(false);
    }
  };

  const selectedTemplate = templates?.find(t => t?.id === formData?.templateId);

  const handleTemplateSelect = (template) => {
    setFormData({
      ...formData,
      templateId: template?.id,
      targetValue: template?.default_target,
      durationDays: template?.default_duration,
      customTitle: template?.title,
      customDescription: template?.description
    });
    setStep(2);
  };

  const handleCreateDuel = async () => {
    if (!formData?.opponentId || !formData?.stakeId) return;
    if (!formData?.isCustom && !selectedTemplate) return;

    setLoading(true);
    try {
      await duelService?.createDuel(userId, {
        opponentId: formData?.opponentId,
        title: formData?.customTitle,
        description: formData?.customDescription,
        metric: formData?.isCustom ? 'daily_minimum' : selectedTemplate?.metric,
        targetValue: formData?.targetValue,
        durationDays: formData?.durationDays,
        stakeId: formData?.stakeId,
        verificationMode: formData?.verificationMode,
        isCustom: formData?.isCustom,
        customRules: formData?.customRules,
        customReward: formData?.customReward,
        customConsequence: formData?.customConsequence,
        verificationTimerMinutes: formData?.verificationTimerMinutes,
        requireDualCamera: formData?.requireDualCamera
      });
      onDuelCreated();
      resetForm();
    } catch (error) {
      console.error('Error creating duel:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setSearchTerm('');
    setSearchResults([]);
    setFormData({
      templateId: null,
      opponentId: null,
      targetValue: 0,
      durationDays: 7,
      stakeId: null,
      customTitle: '',
      customDescription: '',
      workoutsPerWeek: 3,
      specificDays: [],
      isCustom: false,
      customRules: '',
      customReward: '',
      customConsequence: '',
      verificationMode: 'auto',
      verificationTimerMinutes: 2,
      requireDualCamera: false
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
        onClick={handleClose}
      />
      
      <div className="relative w-full max-w-3xl bg-card rounded-xl shadow-wellness-lg animate-scale-in elevation-3 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border/40">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Crear Duelo</h2>
            <p className="text-sm text-muted-foreground">Paso {step} de 3</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Step 1: Choose Template or Custom */}
          {step === 1 && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-4">Elige el Tipo de Reto</h3>
              
              {/* Custom Challenge Option */}
              <button
                onClick={() => {
                  setFormData({ ...formData, isCustom: true });
                  setStep(2);
                }}
                className="w-full p-6 mb-4 text-left border-2 border-primary rounded-xl hover:border-primary hover:bg-primary/10 transition-all"
              >
                <div className="flex items-center space-x-3 mb-2">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <Icon name="Edit3" size={24} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground text-lg">✨ Reto Personalizado</h4>
                    <p className="text-sm text-muted-foreground">Crea tu propio desafío único</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">Define tus propias reglas, recompensas y consecuencias</p>
              </button>

              <div className="relative mb-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-card text-muted-foreground">o elige una plantilla</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates?.map((template) => (
                  <button
                    key={template?.id}
                    onClick={() => handleTemplateSelect(template)}
                    className="p-4 text-left border-2 border-border rounded-xl hover:border-primary hover:bg-primary/5 transition-all"
                  >
                    <div className="flex items-center space-x-3 mb-2">
                      <div className={`w-10 h-10 rounded-full bg-${template?.color}/10 flex items-center justify-center`}>
                        <Icon name={template?.icon} size={20} className={`text-${template?.color}`} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground">{template?.title}</h4>
                        <p className="text-xs text-muted-foreground">{template?.unit}</p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">{template?.description}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Configure Challenge */}
          {step === 2 && (
            <div className="space-y-6">
              {formData?.isCustom ? (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Configura tu Reto Personalizado</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Título del Reto</label>
                    <Input
                      value={formData?.customTitle}
                      onChange={(e) => setFormData({ ...formData, customTitle: e?.target?.value })}
                      placeholder="Ej: 30 días sin azúcar"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Descripción</label>
                    <textarea
                      value={formData?.customDescription}
                      onChange={(e) => setFormData({ ...formData, customDescription: e?.target?.value })}
                      placeholder="Describe el reto en detalle..."
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Duración (días)</label>
                      <Input
                        type="number"
                        value={formData?.durationDays}
                        onChange={(e) => setFormData({ ...formData, durationDays: parseInt(e?.target?.value) })}
                        min={1}
                        max={30}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Meta</label>
                      <Input
                        type="number"
                        value={formData?.targetValue}
                        onChange={(e) => setFormData({ ...formData, targetValue: parseInt(e?.target?.value) })}
                        min={1}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Reglas (texto libre)</label>
                    <textarea
                      value={formData?.customRules}
                      onChange={(e) => setFormData({ ...formData, customRules: e?.target?.value })}
                      placeholder="Ej: No consumir azúcar refinada, bebidas azucaradas permitidas solo los domingos..."
                      className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      rows={3}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Recompensa / Consecuencia</label>
                    <Input
                      value={formData?.customReward}
                      onChange={(e) => setFormData({ ...formData, customReward: e?.target?.value })}
                      placeholder="Ej: El ganador elige la película del viernes"
                    />
                  </div>

                  {/* Verification Mode Selection */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Modo de Verificación</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setFormData({ ...formData, verificationMode: 'auto' })}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          formData?.verificationMode === 'auto' ?'border-primary bg-primary/10' :'border-border hover:border-primary/50'
                        }`}
                      >
                        <Icon name="Zap" size={20} className="mx-auto mb-1" />
                        <p className="text-sm font-medium">Automático</p>
                      </button>
                      <button
                        onClick={() => setFormData({ ...formData, verificationMode: 'realtime' })}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          formData?.verificationMode === 'realtime' ?'border-error bg-error/10' :'border-border hover:border-error/50'
                        }`}
                      >
                        <Icon name="Camera" size={20} className="mx-auto mb-1" />
                        <p className="text-sm font-medium">Tiempo Real</p>
                      </button>
                    </div>
                  </div>

                  {/* Real-time Verification Options */}
                  {formData?.verificationMode === 'realtime' && (
                    <div className="p-4 bg-error/10 rounded-xl border border-error/30 space-y-3">
                      <div className="flex items-start space-x-2">
                        <Icon name="AlertCircle" size={16} className="text-error mt-0.5" />
                        <p className="text-sm text-foreground">Modo de alta exigencia: notificación sorpresa con timer limitado</p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-foreground mb-1">Timer (minutos)</label>
                          <Input
                            type="number"
                            value={formData?.verificationTimerMinutes}
                            onChange={(e) => setFormData({ ...formData, verificationTimerMinutes: parseInt(e?.target?.value) })}
                            min={1}
                            max={10}
                            size="sm"
                          />
                        </div>
                        <div className="flex items-end">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData?.requireDualCamera}
                              onChange={(e) => setFormData({ ...formData, requireDualCamera: e?.target?.checked })}
                              className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                            />
                            <span className="text-sm text-foreground">Dual camera</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={() => setStep(3)}
                    disabled={!formData?.customTitle || formData?.targetValue <= 0}
                    fullWidth
                  >
                    Continuar
                  </Button>
                </div>
              ) : (
                // Existing template configuration
                <div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-4">Elige tu Oponente</h3>
                    
                    {/* Search Bar */}
                    <div className="flex space-x-3 mb-4">
                      <div className="flex-1">
                        <Input
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e?.target?.value)}
                          placeholder="Buscar por nombre o email..."
                          onKeyPress={(e) => e?.key === 'Enter' && handleSearchOpponents()}
                        />
                      </div>
                      <Button
                        onClick={handleSearchOpponents}
                        disabled={!searchTerm?.trim() || searching}
                        loading={searching}
                        iconName="Search"
                        size="sm"
                      >
                        Buscar
                      </Button>
                    </div>

                    {/* Search Results */}
                    {searchResults?.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-muted-foreground mb-2">Resultados de Búsqueda</h4>
                        <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                          {searchResults?.map((user) => (
                            <button
                              key={user?.id}
                              onClick={() => {
                                setFormData({ ...formData, opponentId: user?.id });
                                setSearchResults([]);
                                setSearchTerm('');
                              }}
                              className={`p-3 flex items-center space-x-3 border-2 rounded-lg transition-all ${
                                formData?.opponentId === user?.id
                                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                              }`}
                            >
                              {user?.avatar_url ? (
                                <img src={user?.avatar_url} alt={user?.full_name} className="w-10 h-10 rounded-full" />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                  <span className="text-sm font-semibold text-primary">
                                    {user?.full_name?.charAt(0)?.toUpperCase()}
                                  </span>
                                </div>
                              )}
                              <div className="text-left">
                                <p className="font-medium text-foreground">{user?.full_name}</p>
                                <p className="text-xs text-muted-foreground">{user?.total_points} puntos</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Friends List */}
                    <div>
                      <h4 className="text-sm font-semibold text-muted-foreground mb-2">Tus Amigos</h4>
                      <div className="grid grid-cols-1 gap-3 max-h-64 overflow-y-auto">
                        {friends?.length > 0 ? (
                          friends?.map((friend) => (
                            <button
                              key={friend?.id}
                              onClick={() => setFormData({ ...formData, opponentId: friend?.id })}
                              className={`p-4 flex items-center space-x-3 border-2 rounded-xl transition-all ${
                                formData?.opponentId === friend?.id
                                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                              }`}
                            >
                              {friend?.avatar_url ? (
                                <img src={friend?.avatar_url} alt={friend?.full_name} className="w-10 h-10 rounded-full" />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                  <span className="text-sm font-semibold text-primary">
                                    {friend?.full_name?.charAt(0)?.toUpperCase()}
                                  </span>
                                </div>
                              )}
                              <div className="text-left">
                                <p className="font-medium text-foreground">{friend?.full_name}</p>
                                <p className="text-xs text-muted-foreground">{friend?.total_points} puntos</p>
                              </div>
                            </button>
                          ))
                        ) : (
                          <div className="text-center py-8">
                            <Icon name="Users" size={40} className="text-muted-foreground mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground">No tienes amigos aún</p>
                            <p className="text-xs text-muted-foreground mt-1">Usa la búsqueda para encontrar oponentes</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      type="number"
                      label="Meta"
                      value={formData?.targetValue}
                      onChange={(e) => setFormData({ ...formData, targetValue: parseFloat(e?.target?.value) })}
                      description={selectedTemplate?.unit}
                    />
                    <Input
                      type="number"
                      label="Duración (días)"
                      value={formData?.durationDays}
                      onChange={(e) => setFormData({ ...formData, durationDays: parseInt(e?.target?.value) })}
                      min={1}
                      max={30}
                    />
                  </div>

                  {/* Workouts/Training Selection based on duel type */}
                  {(selectedTemplate?.metric === 'workouts' || selectedTemplate?.metric === 'training_sessions') && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Entrenamientos por Semana
                      </label>
                      <div className="grid grid-cols-4 gap-2">
                        {[1, 2, 3, 4, 5, 6, 7]?.map((num) => (
                          <button
                            key={num}
                            onClick={() => setFormData({ ...formData, workoutsPerWeek: num })}
                            className={`p-3 rounded-lg border-2 font-semibold transition-all ${
                              formData?.workoutsPerWeek === num
                                ? 'border-primary bg-primary/10 text-primary' :'border-border text-muted-foreground hover:border-primary/50'
                            }`}
                          >
                            {num}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Specific Days Selection */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Días Específicos (Opcional)
                    </label>
                    <div className="grid grid-cols-7 gap-2">
                      {['L', 'M', 'X', 'J', 'V', 'S', 'D']?.map((day, index) => {
                        const dayValue = index + 1;
                        const isSelected = formData?.specificDays?.includes(dayValue);
                        return (
                          <button
                            key={day}
                            onClick={() => {
                              const newDays = isSelected
                                ? formData?.specificDays?.filter(d => d !== dayValue)
                                : [...(formData?.specificDays || []), dayValue];
                              setFormData({ ...formData, specificDays: newDays });
                            }}
                            className={`p-3 rounded-lg border-2 font-semibold transition-all ${
                              isSelected
                                ? 'border-primary bg-primary/10 text-primary' :'border-border text-muted-foreground hover:border-primary/50'
                            }`}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Selecciona días específicos o déjalo vacío para cualquier día
                    </p>
                  </div>

                  <div className="flex space-x-3">
                    <Button variant="outline" onClick={() => setStep(1)}>
                      Atrás
                    </Button>
                    <Button 
                      onClick={() => setStep(3)}
                      disabled={!formData?.opponentId}
                      fullWidth
                    >
                      Siguiente: Elegir Stake
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Choose Stake */}
          {step === 3 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Elige el Stake (Consecuencia)</h3>
              
              {['light', 'medium', 'spicy']?.map((severity) => (
                <div key={severity}>
                  <h4 className="text-sm font-semibold text-muted-foreground mb-3 uppercase">
                    {severity === 'light' ? '😊 Ligeros' : severity === 'medium' ? '🔥 Medios' : '🌶️ Picantes'}
                  </h4>
                  <div className="grid grid-cols-1 gap-3">
                    {stakes?.filter(s => s?.severity === severity)?.map((stake) => (
                      <button
                        key={stake?.id}
                        onClick={() => setFormData({ ...formData, stakeId: stake?.id })}
                        className={`p-4 text-left border-2 rounded-xl transition-all ${
                          formData?.stakeId === stake?.id
                            ? 'border-warning bg-warning/5' :'border-border hover:border-warning/50'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <Icon name={stake?.icon} size={20} className="text-warning" />
                          <div>
                            <h5 className="font-semibold text-foreground">{stake?.title}</h5>
                            <p className="text-sm text-muted-foreground">{stake?.description}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              <div className="flex space-x-3">
                <Button variant="outline" onClick={() => setStep(2)}>
                  Atrás
                </Button>
                <Button 
                  onClick={handleCreateDuel}
                  disabled={!formData?.stakeId || loading}
                  loading={loading}
                  fullWidth
                  iconName="Swords"
                >
                  ⚔️ Retar Ahora
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default CreateDuelModal;
